let initPromise: Promise<void> | null = null

/**
 * Lazily initialize `solana-wallets-vue` exactly once.
 *
 * Replaces the old `app/plugins/solana.client.ts`, which ran eagerly on every
 * page load and pulled the whole Solana stack (`@solana/web3.js`, the
 * Phantom/Solflare/Ledger adapters and WalletConnect — ~126 kB gzip) into the
 * first-paint bundle. Only `LoginModal`, the profile page and the SIWS path in
 * `useSupabaseAuth` ever call `useWallet()`, so init is deferred to those
 * callsites instead.
 *
 * `solana-wallets-vue` and the adapters are imported dynamically here on
 * purpose: this module is statically imported by `app.vue` (for the LoginModal
 * async loader), so a static import would put Solana back into the eager graph.
 *
 * `autoConnect` is deliberately OFF. With it on, the persisted `walletName`
 * (VueUse `useLocalStorage('walletName')`, written by `select()` the moment a
 * user taps a wallet) is replayed on every load, because `app.vue` mounts
 * `<LoginModal />` on every page and re-runs this init. (Since #855 that mount
 * happens on idle rather than during boot — later, but still every load, so the
 * hazard below is unchanged.) `solana-wallets-vue`
 * then calls `adapter.connect()` — NOT the adapter's own `autoConnect()`, which
 * exists precisely to skip the `Loadable` state — and on iOS Safari Phantom is
 * `Loadable`, so `connect()` sets `window.location.href` to the Phantom
 * universal link and returns WITHOUT throwing. Nothing clears `walletName`, so
 * an iOS user with no Phantom app who taps the card once is redirected to
 * phantom.app on every subsequent visit, permanently, with no in-app escape.
 *
 * With autoConnect off nothing calls `connect()` unprompted, so already-stuck
 * users are freed on their next load (the stale key just sits there, inert).
 * The cost is small: both consumers (`LoginModal`, `profile.vue`) already call
 * `connect()` on the user's own tap, and a previously-trusted origin reconnects
 * silently there. The Supabase auth session is entirely separate — it lives in
 * `localStorage` via `persistSession` and is restored by `getSession()`, so no
 * logged-in user is affected.
 */
export function ensureSolanaWalletInitialized(): Promise<void> {
  if (initPromise) return initPromise
  initPromise = (async () => {
    const [{ initWallet }, { createSolanaWalletAdapters }] = await Promise.all([
      import('solana-wallets-vue'),
      import('../config/solana'),
    ])
    const config = useRuntimeConfig()
    const isLocal = config.public.appUrl?.includes('localhost')
    initWallet({
      wallets: createSolanaWalletAdapters(),
      autoConnect: false,
      cluster: isLocal ? 'devnet' : 'mainnet-beta',
    })
  })()
  return initPromise
}
